// web/src/services/usageTracker.ts
// Anti-abuse usage tracking with browser fingerprinting and multi-store persistence.
// Stores usage data in localStorage + IndexedDB + cookie, keyed by device fingerprint.
// Same-browser incognito sessions produce the same fingerprint (canvas/WebGL are stable).

import { getDeviceFingerprint } from './deviceFingerprint';

const LEGACY_KEY = 'frame_usage';
const DAILY_LIMIT = 25;
const RESET_INTERVAL_MS = 24 * 60 * 60 * 1000; // 24 hours
const IDB_NAME = 'frame_usage_db';
const IDB_STORE = 'usage';
const COOKIE_NAME = 'fru';

interface UsageData {
  totalPhotosScored: number;
  sessionsCompleted: number;
  firstUseDate: string;
  lastUseDate: string;
  dailyPhotosScored: number;
  dailyResetDate: string;
}

let fingerprint: string | null = null;

function getDefault(): UsageData {
  const now = new Date().toISOString();
  return {
    totalPhotosScored: 0,
    sessionsCompleted: 0,
    firstUseDate: now,
    lastUseDate: now,
    dailyPhotosScored: 0,
    dailyResetDate: now,
  };
}

function maybeResetDaily(data: UsageData): UsageData {
  const resetTime = new Date(data.dailyResetDate).getTime();
  if (isNaN(resetTime) || Date.now() - resetTime > RESET_INTERVAL_MS) {
    data.dailyPhotosScored = 0;
    data.dailyResetDate = new Date().toISOString();
  }
  return data;
}

function storageKey(): string {
  return fingerprint ? `frame_usage_${fingerprint.slice(0, 12)}` : LEGACY_KEY;
}

// ─── localStorage helpers ──────────────────────────────────
function lsRead(): UsageData | null {
  try {
    const raw = localStorage.getItem(storageKey());
    if (!raw) {
      // Try legacy key for backward compat
      const legacy = localStorage.getItem(LEGACY_KEY);
      if (legacy) return JSON.parse(legacy);
      return null;
    }
    return JSON.parse(raw);
  } catch { return null; }
}

function lsWrite(data: UsageData): void {
  try {
    const json = JSON.stringify(data);
    localStorage.setItem(storageKey(), json);
    // Also write to legacy key for backward compat
    localStorage.setItem(LEGACY_KEY, json);
  } catch { /* quota exceeded or blocked */ }
}

// ─── Cookie helpers ────────────────────────────────────────
function cookieRead(): { d: number; r: string } | null {
  try {
    const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${COOKIE_NAME}=([^;]*)`));
    if (!match) return null;
    return JSON.parse(decodeURIComponent(match[1]));
  } catch { return null; }
}

function cookieWrite(daily: number, resetDate: string): void {
  try {
    const val = encodeURIComponent(JSON.stringify({ d: daily, r: resetDate }));
    document.cookie = `${COOKIE_NAME}=${val};max-age=86400;path=/;SameSite=Lax`;
  } catch { /* cookies disabled */ }
}

// ─── IndexedDB helpers ─────────────────────────────────────
function idbOpen(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function idbRead(): Promise<UsageData | null> {
  try {
    const db = await idbOpen();
    return new Promise((resolve) => {
      const tx = db.transaction(IDB_STORE, 'readonly');
      const store = tx.objectStore(IDB_STORE);
      const key = fingerprint || 'default';
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result ?? null);
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

async function idbWrite(data: UsageData): Promise<void> {
  try {
    const db = await idbOpen();
    const tx = db.transaction(IDB_STORE, 'readwrite');
    const store = tx.objectStore(IDB_STORE);
    const key = fingerprint || 'default';
    store.put(data, key);
  } catch { /* IDB blocked or unavailable */ }
}

// ─── Merge: take the record with the highest daily count ───
function mergeRecords(...records: (UsageData | null)[]): UsageData {
  const valid = records.filter((r): r is UsageData => r !== null).map(r => maybeResetDaily({ ...r }));
  if (valid.length === 0) return getDefault();
  // Pick the record with the highest daily count (defeats clearing one store)
  valid.sort((a, b) => b.dailyPhotosScored - a.dailyPhotosScored);
  return valid[0];
}

// ─── Public API (unchanged interface) ──────────────────────
export const UsageTracker = {
  async init(): Promise<void> {
    try {
      fingerprint = await getDeviceFingerprint();
    } catch {
      fingerprint = null;
    }
  },

  get(): UsageData {
    // Synchronous fast path: localStorage + cookie only
    const ls = lsRead();
    const ck = cookieRead();
    const ckData: UsageData | null = ck ? {
      ...getDefault(),
      dailyPhotosScored: ck.d,
      dailyResetDate: ck.r,
    } : null;
    return mergeRecords(ls, ckData);
  },

  recordSession(photoCount: number): void {
    const data = this.get();
    data.totalPhotosScored += photoCount;
    data.dailyPhotosScored += photoCount;
    data.sessionsCompleted += 1;
    data.lastUseDate = new Date().toISOString();
    // Write to all stores
    lsWrite(data);
    cookieWrite(data.dailyPhotosScored, data.dailyResetDate);
    idbWrite(data).catch(() => {}); // fire-and-forget
  },

  requiresAuth(): boolean {
    return this.get().dailyPhotosScored >= DAILY_LIMIT;
  },

  photosRemaining(): number {
    return Math.max(0, DAILY_LIMIT - this.get().dailyPhotosScored);
  },

  reset(): void {
    try { localStorage.removeItem(storageKey()); } catch {}
    try { localStorage.removeItem(LEGACY_KEY); } catch {}
    try { document.cookie = `${COOKIE_NAME}=;max-age=0;path=/`; } catch {}
    idbWrite(getDefault()).catch(() => {});
  },
};
